import React, { useRef, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import $ from "jquery";
import moment from "moment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import "bootstrap/dist/css/bootstrap.min.css";
import "daterangepicker/daterangepicker.css";
import "daterangepicker";
import "select2";
import "select2/dist/css/select2.min.css";
import { seDashboardAPI } from "../services/seDashboardApi";
import { masterDataAPI, territoryMasterAPI, userAPI } from "../services/api";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import DashboardExportDropdown from "../components/common/DashboardExportDropdown";
import {
  formatDateDDMMYYYY,
  exportJsonToExcel,
  exportRowsToCsv,
  exportRowsToPdf,
} from "../utils/dashboardExportUtils";
import {
  getChartDateInputWidthPx,
  getDashboardDateRangePresets,
} from "../utils/dashboardDateRangePicker";
import {
  filterGeoOptionsForDashboardUser,
  filterMasterCountriesForDashboardUser,
  getLoggedInUserFromStorage,
  getUserDashboardLockedMarket,
  isSeDashboardAllCountriesUser,
  isUserGeoAllScopeForDashboard,
  resolveGeoFilterSelection,
  isGeoAllSelectionToken,
  USER_DEFAULT_GEO_CODE,
} from "../utils/userTerritoryScope";
import {
  extractRegionsFromResponse,
  extractZonesFromResponse,
  formatZoneOptionLabel,
  geoFilterArraysEqual,
  isExportMarketSelected,
  mapExportZonesToOptions,
  mapRegionToOption,
  mapTerritoryToOption,
  mapZoneToOption,
  normalizeRegionRow,
  normalizeTerritoryRow,
  normalizeZoneRow,
  parseApiEnvelope,
  resolveRegionGeoCandidates,
  expandZoneFilterCodes,
  getCountryCodesForSelectedZones,
  resolveZoneCodeForCountry,
  findZoneOption,
} from "../utils/dashboardGeoFilterUtils";
import "./Dashboard.css";

const GEO_ALL_OPTION = { id: USER_DEFAULT_GEO_CODE, text: "All" };
const GEO_DROPDOWN_LOAD_MIN_MS = 150;

const isGeoAllOptionRow = (id, text) => {
  const sid = String(id ?? "").trim().toLowerCase();
  const label = String(text ?? "").trim().toLowerCase();
  return (
    sid === "all" ||
    sid === String(USER_DEFAULT_GEO_CODE).toLowerCase() ||
    label === "all"
  );
};

const isGeoAllFilterValue = (val) =>
  Array.isArray(val) &&
  val.length > 0 &&
  val.every((id) => isGeoAllSelectionToken(id));

const selectionIncludesGeoAll = (raw, optionList) =>
  (Array.isArray(raw) ? raw : []).some((id) => {
    const sid = String(id ?? "").trim();
    if (isGeoAllSelectionToken(sid)) return true;
    const opt = (optionList || []).find((o) => String(o?.id ?? "").trim() === sid);
    return isGeoAllOptionRow(sid, opt?.text);
  });

// Plugin to display each segment's count value inside its colored arc (black text; skip label when value is 0)
const pieChartValuePlugin = {
  id: "pieChartValuePlugin",
  afterDatasetsDraw: (chart) => {
    if (chart.config.type !== "pie" && chart.config.type !== "doughnut") return;

    const ctx = chart.ctx;
    const data = chart.data.datasets[0].data;
    const meta = chart.getDatasetMeta(0);

    meta.data.forEach((element, index) => {
      // Allow overriding displayed labels (e.g., hide placeholder segment label).
      const displayValues = chart?.config?.options?.plugins?.pieChartValuePlugin?.displayValues;
      const rawValue = data[index];
      const display = Array.isArray(displayValues) ? displayValues[index] : rawValue;
      if (display === 0 || display == null) return;

      const position = element.tooltipPosition();

      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = "bold 14px Arial";
      ctx.fillStyle = "#000000";
      ctx.fillText(String(display), position.x, position.y);
      ctx.restore();
    });
  },
};

// Plugin for donut center text (used for "no data" state)
const donutCenterTextPlugin = {
  id: "donutCenterTextPlugin",
  afterDraw: (chart) => {
    if (chart.config.type !== "pie" && chart.config.type !== "doughnut") return;
    const opts = chart?.config?.options?.plugins?.donutCenterTextPlugin;
    if (!opts || opts.enabled !== true) return;

    const lines = Array.isArray(opts.textLines) ? opts.textLines.filter(Boolean) : [];
    if (lines.length === 0) return;

    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = opts.color || "#6c757d";

    const fontSize = Number(opts.fontSize || 13);
    const fontWeight = opts.fontWeight || 700;
    const fontFamily = opts.fontFamily || "Arial";
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;

    const lineHeight = Number(opts.lineHeight || Math.round(fontSize * 1.25));
    const startY = centerY - ((lines.length - 1) * lineHeight) / 2;

    lines.forEach((t, i) => {
      ctx.fillText(String(t), centerX, startY + i * lineHeight);
    });

    ctx.restore();
  },
};

// Plugin to display value labels on bar chart, positioned just above each bar top
const barChartValuePlugin = {
  id: "barChartValuePlugin",
  afterDatasetsDraw: (chart) => {
    if (chart.config.type !== "bar") return;

    const ctx = chart.ctx;
    const data = chart.data.datasets[0].data;
    const meta = chart.getDatasetMeta(0);
    const offsetPx = 4; // Minimal spacing above bar

    meta.data.forEach((element, index) => {
      const value = data[index];
      if (value === null || value === undefined) return;

      const { x } = element.tooltipPosition();
      // element.y is the top of the bar (smaller canvas y) in Chart.js vertical bar
      const barTop = element.y !== undefined ? element.y : element.base - Math.abs(element.height);

      ctx.save();
      ctx.fillStyle = "#333";
      ctx.font = "bold 12px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";

      const textY = barTop - offsetPx;
      ctx.fillText(value, x, textY);
      ctx.restore();
    });
  },
};

// Register Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  pieChartValuePlugin,
  barChartValuePlugin,
  donutCenterTextPlugin
);

const getDashboardFinancialYearRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const fyStartYear = month < 3 ? year - 1 : year;
  const fyEndYear = month < 3 ? year : year + 1;
  const start = `${fyStartYear}-04-01`;
  const end = `${fyEndYear}-03-31`;
  const fmt = (y, m, d) => {
    const dd = String(d).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    return `${dd}-${mm}-${y}`;
  };
  const displayText = `${fmt(fyStartYear, 4, 1)} - ${fmt(fyEndYear, 3, 31)}`;
  return { dateRangeStart: start, dateRangeEnd: end, dateRange: displayText };
};

const initDashboardCardDateRangePicker = (inputEl, { initialDisplay, onApply }) => {
  if (!inputEl) return () => {};
  const $input = $(inputEl);
  if ($input.data("daterangepicker")) {
    $input.off("apply.daterangepicker");
    $input.off("cancel.daterangepicker");
    $input.data("daterangepicker").remove();
  }
  $input.daterangepicker({
    autoUpdateInput: false,
    locale: {
      cancelLabel: "Clear",
      applyLabel: "Apply",
      format: "DD-MM-YYYY",
    },
    opens: "left",
    drops: "down",
    showDropdowns: true,
    ranges: getDashboardDateRangePresets(),
  });
  if (initialDisplay) $input.val(initialDisplay);
  $input.on("apply.daterangepicker", function (ev, picker) {
    const displayText =
      picker.startDate.format("DD-MM-YYYY") +
      " - " +
      picker.endDate.format("DD-MM-YYYY");
    const start = picker.startDate.format("YYYY-MM-DD");
    const end = picker.endDate.format("YYYY-MM-DD");
    $(this).val(displayText);
    onApply?.({ start, end, display: displayText });
  });
  $input.on("cancel.daterangepicker", function () {
    $(this).val("");
    onApply?.({ start: "", end: "", display: "" });
  });
  return () => {
    if ($input.data("daterangepicker")) {
      $input.off("apply.daterangepicker");
      $input.off("cancel.daterangepicker");
      $input.data("daterangepicker").remove();
    }
  };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const pieChartRef = useRef(null);
  const barChartRef = useRef(null);
  const topCustomersChartRef = useRef(null);
  const dateRangePickerRef = useRef(null);
  const filterDateRangePickerRef = useRef(null);
  const srStatusCardDatePickerRef = useRef(null);
  const srCategoryCardDatePickerRef = useRef(null);
  const topCustomersCardDatePickerRef = useRef(null);
  const loadSrStatusBreakupRef = useRef(null);
  const loadSrCategoryBreakupRef = useRef(null);
  const loadTopCustomersBreakupRef = useRef(null);

  const wrapTickLabel = (raw, maxLineLen = 14, maxLines = 3) => {
    const text = String(raw ?? "").trim();
    if (!text) return "";
    const parts = text.split(/\s+/).filter(Boolean);
    const lines = [];
    let cur = "";
    for (const p of parts) {
      const next = cur ? `${cur} ${p}` : p;
      if (next.length <= maxLineLen) cur = next;
      else {
        if (cur) lines.push(cur);
        cur = p;
      }
      if (lines.length >= maxLines) break;
    }
    if (lines.length < maxLines && cur) lines.push(cur);
    return lines.length > 1 ? lines : lines[0];
  };

  // Refs for select2 dropdowns
  const statusSelectRef = useRef(null);
  const bvSelectRef = useRef(null);
  /** Latest multiselect values — read on Filter/apply so clicks are not stale vs React state. */
  const statusFilterRef = useRef([]);
  const bvFilterRef = useRef([]);
  /** Prevents duplicate dashboard-data on mount (date filter effect + StrictMode). */
  const skipDateFilterDashboardLoadRef = useRef(true);
  const prevExportCascadeRef = useRef(null);
  const userGeographyRef = useRef(null);
  const zoneOptionsRef = useRef([]);
  const regionOptionsRef = useRef([]);
  const statusOptionsRef = useRef([]);
  const bvOptionsRef = useRef([]);
  const dashboardFilterSeqRef = useRef(0);
  const geoLoadGenRef = useRef({ country: 0, zone: 0, region: 0, territory: 0 });

  const loginUser = useMemo(() => getLoggedInUserFromStorage(), []);

  /** M001 → Domestic, M002 → Export; null = user may choose market. */
  const lockedMarketCode = useMemo(
    () => getUserDashboardLockedMarket(getLoggedInUserFromStorage()),
    []
  );

  // Date range filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRangeText, setDateRangeText] = useState("");

  // Current financial year range: 01-Apr to 31-Mar
  const getCurrentFinancialYearRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0 = Jan, 3 = Apr
    const fyStartYear = month < 3 ? year - 1 : year;
    const fyEndYear = month < 3 ? year : year + 1;
    const start = `${fyStartYear}-04-01`;
    const end = `${fyEndYear}-03-31`;
    const fmt = (y, m, d) => {
      const dd = String(d).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      return `${dd}-${mm}-${y}`;
    };
    const displayText = `${fmt(fyStartYear, 4, 1)} - ${fmt(fyEndYear, 3, 31)}`;
    return { dateRangeStart: start, dateRangeEnd: end, dateRange: displayText };
  };

  const defaultFY = getCurrentFinancialYearRange();
  // Filter states - default date range to current financial year (01-Apr to 31-Mar)
  const [filters, setFilters] = useState({
    dateRange: defaultFY.dateRange,
    dateRangeStart: defaultFY.dateRangeStart,
    dateRangeEnd: defaultFY.dateRangeEnd,
    // Multi-select arrays for Status and Product Vertical
    status: [],
    bv: [],
    invoiceNo: "",
    market: "",
    country: [],
    zone: [],
    region: [],
    territory: [],
  });

  // Dropdown data states - Status from Greenlam.SRSTATUSMASTER, Business Vertical from Greenlam.CATEGORYMASTER
  const [statusOptions, setStatusOptions] = useState([]);
  const [marketOptions, setMarketOptions] = useState([]);
  const [bvOptions, setBvOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [territoryOptions, setTerritoryOptions] = useState([]);
  const [geoLoading, setGeoLoading] = useState({
    country: false,
    zone: false,
    region: false,
    territory: false,
  });
  const [invoiceSuggestions, setInvoiceSuggestions] = useState([]);
  const [showInvoiceSuggestions, setShowInvoiceSuggestions] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [userGeography, setUserGeography] = useState(null);

  useEffect(() => {
    userGeographyRef.current = userGeography;
  }, [userGeography]);

  useEffect(() => {
    zoneOptionsRef.current = zoneOptions;
  }, [zoneOptions]);

  useEffect(() => {
    regionOptionsRef.current = regionOptions;
  }, [regionOptions]);

  useEffect(() => {
    statusOptionsRef.current = statusOptions;
  }, [statusOptions]);

  useEffect(() => {
    bvOptionsRef.current = bvOptions;
  }, [bvOptions]);

  const resolveMultiSelectApiTokens = (ids, options) => {
    const tokens = new Set();
    (Array.isArray(ids) ? ids : []).forEach((id) => {
      const code = String(id ?? "").trim();
      if (!code) return;
      tokens.add(code);
      const opt = (Array.isArray(options) ? options : []).find(
        (o) => String(o.id ?? "").trim() === code
      );
      const label = String(opt?.text ?? "").trim();
      if (label) tokens.add(label);
    });
    return [...tokens];
  };

  const loadUserGeography = async () => {
    const id = loginUser?.userIdCode || loginUser?.UserIdCode;
    if (!id) return;
    try {
      const res = await userAPI.getGeography(id);
      const payload = res?.data ?? res?.Data ?? res;
      userGeographyRef.current = payload;
      setUserGeography(payload);
    } catch (e) {
      console.error("Error loading user geography for dashboard filters:", e);
    }
  };

  const isExportCascade = useMemo(
    () => isExportMarketSelected(filters.market, marketOptions),
    [filters.market, marketOptions]
  );
  const isExportCascadeRef = useRef(isExportCascade);
  isExportCascadeRef.current = isExportCascade;

  const zoneOptionIdsKey = useMemo(
    () =>
      (zoneOptions || [])
        .map((o) => String(o?.id ?? "").trim())
        .filter(Boolean)
        .join("|"),
    [zoneOptions]
  );

  /** Export flow: countries linked to selected zone(s). */
  const exportCountryOptions = useMemo(() => {
    if (!isExportCascade || !filters.zone?.length) return [];
    if (isGeoAllFilterValue(filters.zone)) return [GEO_ALL_OPTION];
    const codes = getCountryCodesForSelectedZones(filters.zone, zoneOptions);
    if (!codes.size) return countryOptions;
    return countryOptions.filter(
      (c) => codes.has(c.id) || isGeoAllSelectionToken(c.id)
    );
  }, [isExportCascade, filters.zone, zoneOptions, countryOptions]);

  const runGeoDropdownLoad = async (level, task) => {
    geoLoadGenRef.current[level] += 1;
    const generation = geoLoadGenRef.current[level];
    const isCurrent = () => geoLoadGenRef.current[level] === generation;
    setGeoLoading((prev) => ({ ...prev, [level]: true }));
    const startedAt = Date.now();
    try {
      await task(isCurrent);
    } finally {
      const elapsed = Date.now() - startedAt;
      const remain = GEO_DROPDOWN_LOAD_MIN_MS - elapsed;
      if (remain > 0) await new Promise((r) => setTimeout(r, remain));
      if (isCurrent()) {
        setGeoLoading((prev) => ({ ...prev, [level]: false }));
      }
    }
  };

  const withGeoAllOption = (options, level) => {
    const list = (Array.isArray(options) ? options : []).filter(
      (o) => o && !isGeoAllOptionRow(o.id, o.text)
    );
    const allowAll =
      isUserGeoAllScopeForDashboard(loginUser, level) ||
      (level === "country" && isSeDashboardAllCountriesUser(loginUser));
    return allowAll ? [GEO_ALL_OPTION, ...list] : list;
  };

  const joinGeoFilter = (val) => {
    if (isGeoAllFilterValue(val)) return null;
    if (Array.isArray(val) && val.length) {
      const real = val.filter((id) => !isGeoAllSelectionToken(id));
      const joined = real.filter(Boolean).join(",");
      return joined || null;
    }
    if (val && String(val).trim() && !isGeoAllSelectionToken(val)) {
      return String(val).trim();
    }
    return null;
  };

  const handleGeoFilterChange = (field, value, optionList = []) => {
    const exportCascade = isExportMarketSelected(filters.market, marketOptions);
    const raw = Array.isArray(value)
      ? value.map((x) => String(x ?? "").trim()).filter(Boolean)
      : [];
    const realOnly = raw.filter((id) => {
      const opt = (optionList || []).find((o) => String(o?.id ?? "").trim() === id);
      return !isGeoAllOptionRow(id, opt?.text);
    });
    const pickedAll = selectionIncludesGeoAll(raw, optionList);
    const prevWasAll = isGeoAllFilterValue(filters[field]);

    // Master-data / UI "All" → set child geos to All (same as reports / admin dashboard).
    if (pickedAll && !(prevWasAll && realOnly.length > 0)) {
      const allVal = [USER_DEFAULT_GEO_CODE];
      if (field === "country") {
        setZoneOptions([GEO_ALL_OPTION]);
        setRegionOptions([GEO_ALL_OPTION]);
        setTerritoryOptions([GEO_ALL_OPTION]);
        setFilters((prev) => ({
          ...prev,
          country: allVal,
          zone: allVal,
          region: allVal,
          territory: allVal,
        }));
        return;
      }
      if (field === "zone") {
        setRegionOptions([GEO_ALL_OPTION]);
        setTerritoryOptions([GEO_ALL_OPTION]);
        if (exportCascade) {
          setFilters((prev) => ({
            ...prev,
            zone: allVal,
            country: allVal,
            region: allVal,
            territory: allVal,
          }));
        } else {
          setFilters((prev) => ({
            ...prev,
            zone: allVal,
            region: allVal,
            territory: allVal,
          }));
        }
        return;
      }
      if (field === "region") {
        setTerritoryOptions([GEO_ALL_OPTION]);
        setFilters((prev) => ({ ...prev, region: allVal, territory: allVal }));
        return;
      }
      if (field === "territory") {
        setFilters((prev) => ({ ...prev, territory: allVal }));
        return;
      }
    }

    const selected =
      realOnly.length > 0
        ? realOnly
        : resolveGeoFilterSelection(value, optionList).filter(
            (id) => !isGeoAllSelectionToken(id) && !isGeoAllOptionRow(id)
          );

    setFilters((prev) => {
      if (field === "country") {
        if (exportCascade) {
          return { ...prev, country: selected, region: [], territory: [] };
        }
        return { ...prev, country: selected, zone: [], region: [], territory: [] };
      }
      if (field === "zone") {
        if (exportCascade) {
          return { ...prev, zone: selected, country: [], region: [], territory: [] };
        }
        return { ...prev, zone: selected, region: [], territory: [] };
      }
      if (field === "region") {
        return { ...prev, region: selected, territory: [] };
      }
      if (field === "territory") {
        return { ...prev, territory: selected };
      }
      return prev;
    });
  };

  // Dashboard data from Greenlam.SRTRANSACTION (loaded via API)
  const [srData, setSrData] = useState({
    totalSRCount: 0,
    openSRCount: 0,
    closedSRCount: 0,
    rejectedSRCount: 0,
    specialCaseSRCount: 0,
    queryToRespondSRCount: 0,
    approvedPaymentPendingSRCount: 0,
  });
  const [complaintCategoryData, setComplaintCategoryData] = useState({
    floors: 0,
    interiorCompacts: 0,
    laminate: 0,
    mikasaDoorsAndFrames: 0,
  });
  const [srCategoryData, setSrCategoryData] = useState({
    labels: [],
    srCounts: [],
  });
  const [topCustomersData, setTopCustomersData] = useState({
    customers: [],
    customerCodes: [],
    srCounts: [],
  });
  const [srStatusBreakupData, setSrStatusBreakupData] = useState({
    openSRCount: 0,
    closedSRCount: 0,
    rejectedSRCount: 0,
    specialCaseSRCount: 0,
    approvedPaymentPendingSRCount: 0,
  });
  const [srStatusCardDate, setSrStatusCardDate] = useState({
    dateRange: defaultFY.dateRange,
    dateRangeStart: defaultFY.dateRangeStart,
    dateRangeEnd: defaultFY.dateRangeEnd,
  });
  const [srCategoryCardDate, setSrCategoryCardDate] = useState({
    dateRange: defaultFY.dateRange,
    dateRangeStart: defaultFY.dateRangeStart,
    dateRangeEnd: defaultFY.dateRangeEnd,
  });
  const [topCustomersCardDate, setTopCustomersCardDate] = useState({
    dateRange: defaultFY.dateRange,
    dateRangeStart: defaultFY.dateRangeStart,
    dateRangeEnd: defaultFY.dateRangeEnd,
  });

  // Total SR Count = all except Draft (from API). Fallback to sum of 5 buckets if totalSRCount not provided.
  const totalSRCount =
    srData.totalSRCount != null && srData.totalSRCount !== undefined
      ? srData.totalSRCount
      : srData.openSRCount +
        srData.closedSRCount +
        srData.rejectedSRCount +
        srData.specialCaseSRCount +
        srData.approvedPaymentPendingSRCount;

  const buildDashboardApiParams = React.useCallback(
    (overrideParams = {}, dateOverride = null) => {
      let createdBy = null;
      const u = localStorage.getItem("user");
      if (u) {
        const user = JSON.parse(u);
        createdBy = user.userIdCode || user.UserIdCode || null;
      }
      const params = { createdBy };
      const fromDate =
        overrideParams.fromDate !== undefined
          ? overrideParams.fromDate
          : dateOverride?.start !== undefined
            ? dateOverride.start
            : filters.dateRangeStart;
      const toDate =
        overrideParams.toDate !== undefined
          ? overrideParams.toDate
          : dateOverride?.end !== undefined
            ? dateOverride.end
            : filters.dateRangeEnd;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      const statusList =
        Object.prototype.hasOwnProperty.call(overrideParams, "status")
          ? overrideParams.status
          : statusFilterRef.current;
      const bvList =
        Object.prototype.hasOwnProperty.call(overrideParams, "bv")
          ? overrideParams.bv
          : bvFilterRef.current;
      const countryVal =
        overrideParams.country !== undefined ? overrideParams.country : filters.country;
      const zoneVal = overrideParams.zone !== undefined ? overrideParams.zone : filters.zone;
      const regionVal =
        overrideParams.region !== undefined ? overrideParams.region : filters.region;
      const territoryVal =
        overrideParams.territory !== undefined ? overrideParams.territory : filters.territory;
      const invoiceVal =
        overrideParams.invoiceNo !== undefined ? overrideParams.invoiceNo : filters.invoiceNo;
      if (invoiceVal && String(invoiceVal).trim()) {
        params.invoiceNumber = String(invoiceVal).trim();
      }
      if (Array.isArray(statusList) && statusList.length > 0) {
        const joinedStatus = resolveMultiSelectApiTokens(
          statusList,
          statusOptionsRef.current
        ).join(",");
        if (joinedStatus) params.statusCode = joinedStatus;
      }
      if (Array.isArray(bvList) && bvList.length > 0) {
        const joinedBv = resolveMultiSelectApiTokens(
          bvList,
          bvOptionsRef.current
        ).join(",");
        if (joinedBv) params.productVerticalCode = joinedBv;
      }
      const countryCsv = joinGeoFilter(countryVal);
      const zoneExpanded =
        isExportCascade && Array.isArray(zoneVal) && zoneVal.length
          ? expandZoneFilterCodes(zoneVal, zoneOptionsRef.current)
          : zoneVal;
      const zoneCsv = joinGeoFilter(zoneExpanded);
      const regionCsv = joinGeoFilter(regionVal);
      const territoryCsv = joinGeoFilter(territoryVal);
      if (countryCsv) params.countryCode = countryCsv;
      if (zoneCsv) params.zoneCode = zoneCsv;
      if (regionCsv) params.regionCode = regionCsv;
      if (territoryCsv) params.territoryCode = territoryCsv;
      return params;
    },
    [
      filters.dateRangeStart,
      filters.dateRangeEnd,
      filters.status,
      filters.bv,
      filters.country,
      filters.zone,
      filters.region,
      filters.territory,
      filters.invoiceNo,
      isExportCascade,
    ]
  );

  /** Carry active dashboard filters into /complaints drilldown (date, BV, invoice, status). */
  const buildComplaintsDrilldownQuery = React.useCallback(
    ({
      statusToken,
      category,
      customerCode,
      customerName,
      dateOverride,
      useDashboardStatusWhenNoToken = true,
      queryToRespond,
      specialCase,
    } = {}) => {
      const qp = new URLSearchParams();

      const from =
        dateOverride?.fromDate ??
        filters.dateRangeStart ??
        defaultFY.dateRangeStart;
      const to =
        dateOverride?.toDate ?? filters.dateRangeEnd ?? defaultFY.dateRangeEnd;
      if (from) qp.set("fromDate", from);
      if (to) qp.set("toDate", to);

      const bvList = resolveMultiSelectApiTokens(
        bvFilterRef.current?.length ? bvFilterRef.current : filters?.bv,
        bvOptionsRef.current
      );
      if (bvList.length) qp.set("bv", bvList.join(","));

      const invoice = String(filters?.invoiceNo ?? "").trim();
      if (invoice) qp.set("invoiceNo", invoice);

      if (category) qp.set("category", String(category));
      if (customerCode && String(customerCode).trim()) {
        qp.set("customerCode", String(customerCode).trim());
      }
      if (customerName) qp.set("customer", String(customerName));

      if (queryToRespond === true) {
        qp.set("queryToRespond", "true");
      }
      if (specialCase === true) {
        qp.set("specialCase", "true");
      }

      if (
        statusToken !== undefined &&
        statusToken !== null &&
        String(statusToken).trim() !== ""
      ) {
        qp.set("status", String(statusToken).trim());
      } else if (useDashboardStatusWhenNoToken && !queryToRespond && !specialCase) {
        const selectedStatusIds = Array.isArray(filters?.status)
          ? filters.status.filter(Boolean)
          : [];
        if (
          selectedStatusIds.length > 0 &&
          Array.isArray(statusOptions) &&
          statusOptions.length > 0
        ) {
          const selectedStatusNames = selectedStatusIds
            .map(
              (id) =>
                statusOptions.find((o) => String(o.id) === String(id))?.text ||
                ""
            )
            .map((t) => String(t || "").trim())
            .filter(Boolean);
          if (selectedStatusNames.length) {
            qp.set("status", selectedStatusNames.join(","));
          }
        }
      }

      const qs = qp.toString();
      return qs ? `/complaints?${qs}` : "/complaints";
    },
    [filters, defaultFY, statusOptions]
  );

  const fetchDashboardPayload = React.useCallback(
    async (overrideParams = {}, dateOverride = null) => {
      const params = buildDashboardApiParams(overrideParams, dateOverride);
      try {
        const response = await seDashboardAPI.getDashboardData(params);
        const successFlag = response?.success ?? response?.Success;
        const payload = response?.data ?? response;
        if (successFlag && payload) return payload;
        return null;
      } catch (e) {
        console.error("Error loading dashboard data:", e);
        return null;
      }
    },
    [buildDashboardApiParams]
  );

  const applySrCategoryFromPayload = (payload) => {
    const rawComplaintCategory = payload.complaintCategoryData ?? payload.ComplaintCategoryData;
    const rawSrCategory = payload.srCategoryData ?? payload.SrCategoryData;
    if (
      rawSrCategory &&
      Array.isArray(rawSrCategory.labels ?? rawSrCategory.Labels) &&
      Array.isArray(rawSrCategory.srCounts ?? rawSrCategory.SrCounts)
    ) {
      const labels = rawSrCategory.labels ?? rawSrCategory.Labels ?? [];
      const counts = rawSrCategory.srCounts ?? rawSrCategory.SrCounts ?? [];
      if (labels.length === counts.length) {
        setSrCategoryData({ labels, srCounts: counts });
        return;
      }
    }
    if (rawComplaintCategory) {
      setSrCategoryData({
        labels: ["Quality", "Dispatch", "Installation", "Commercial"],
        srCounts: [
          rawComplaintCategory.floors ?? rawComplaintCategory.Floors ?? 0,
          rawComplaintCategory.interiorCompacts ?? rawComplaintCategory.InteriorCompacts ?? 0,
          rawComplaintCategory.laminate ?? rawComplaintCategory.Laminate ?? 0,
          rawComplaintCategory.mikasaDoorsAndFrames ??
            rawComplaintCategory.MikasaDoorsAndFrames ??
            0,
        ],
      });
      return;
    }
    setSrCategoryData({ labels: ["–"], srCounts: [0] });
  };

  const applyKpiFromPayload = (payload) => {
    const rawSrData = payload?.srData ?? payload?.SrData;
    if (!rawSrData) return;
    setSrData({
      totalSRCount: rawSrData.totalSRCount ?? rawSrData.TotalSRCount ?? 0,
      openSRCount: rawSrData.openSRCount ?? rawSrData.OpenSRCount ?? 0,
      closedSRCount: rawSrData.closedSRCount ?? rawSrData.ClosedSRCount ?? 0,
      rejectedSRCount: rawSrData.rejectedSRCount ?? rawSrData.RejectedSRCount ?? 0,
      specialCaseSRCount:
        rawSrData.specialCaseSRCount ?? rawSrData.SpecialCaseSRCount ?? 0,
      queryToRespondSRCount:
        rawSrData.queryToRespondSRCount ?? rawSrData.QueryToRespondSRCount ?? 0,
      approvedPaymentPendingSRCount:
        rawSrData.approvedPaymentPendingSRCount ??
        rawSrData.ApprovedPaymentPendingSRCount ??
        0,
    });
  };

  const applyStatusBreakupFromPayload = (payload) => {
    const rawSrData = payload?.srData ?? payload?.SrData;
    if (!rawSrData) return;
    setSrStatusBreakupData({
      openSRCount: rawSrData.openSRCount ?? rawSrData.OpenSRCount ?? 0,
      closedSRCount: rawSrData.closedSRCount ?? rawSrData.ClosedSRCount ?? 0,
      rejectedSRCount: rawSrData.rejectedSRCount ?? rawSrData.RejectedSRCount ?? 0,
      specialCaseSRCount:
        rawSrData.specialCaseSRCount ?? rawSrData.SpecialCaseSRCount ?? 0,
      approvedPaymentPendingSRCount:
        rawSrData.approvedPaymentPendingSRCount ??
        rawSrData.ApprovedPaymentPendingSRCount ??
        0,
    });
  };

  const applyTopCustomersFromPayload = (payload) => {
    const rawTopCustomers = payload?.topCustomersData ?? payload?.TopCustomersData;
    if (!rawTopCustomers) return;
    setTopCustomersData({
      customers: rawTopCustomers.customers ?? rawTopCustomers.Customers ?? [],
      customerCodes: rawTopCustomers.customerCodes ?? rawTopCustomers.CustomerCodes ?? [],
      srCounts: rawTopCustomers.srCounts ?? rawTopCustomers.SrCounts ?? [],
    });
  };

  const applyFullDashboardPayload = (payload) => {
    if (!payload) return;
    applyKpiFromPayload(payload);
    applyStatusBreakupFromPayload(payload);
    applySrCategoryFromPayload(payload);
    applyTopCustomersFromPayload(payload);
  };

  /** One API call feeds KPI cards + all charts when they share the same filters/dates. */
  const loadAllDashboardData = React.useCallback(
    async (overrideParams = {}) => {
      const payload = await fetchDashboardPayload(overrideParams);
      applyFullDashboardPayload(payload);
      return payload;
    },
    [fetchDashboardPayload]
  );

  const loadSrStatusBreakup = React.useCallback(
    async (overrideParams = {}) => {
      const fromDate =
        overrideParams.fromDate !== undefined
          ? overrideParams.fromDate
          : srStatusCardDate.dateRangeStart;
      const toDate =
        overrideParams.toDate !== undefined ? overrideParams.toDate : srStatusCardDate.dateRangeEnd;
      const payload = await fetchDashboardPayload(overrideParams, { start: fromDate, end: toDate });
      applyStatusBreakupFromPayload(payload);
    },
    [fetchDashboardPayload, srStatusCardDate.dateRangeStart, srStatusCardDate.dateRangeEnd]
  );

  const loadSrCategoryBreakup = React.useCallback(
    async (overrideParams = {}) => {
      const fromDate =
        overrideParams.fromDate !== undefined
          ? overrideParams.fromDate
          : srCategoryCardDate.dateRangeStart;
      const toDate =
        overrideParams.toDate !== undefined ? overrideParams.toDate : srCategoryCardDate.dateRangeEnd;
      const payload = await fetchDashboardPayload(overrideParams, { start: fromDate, end: toDate });
      if (payload) applySrCategoryFromPayload(payload);
    },
    [fetchDashboardPayload, srCategoryCardDate.dateRangeStart, srCategoryCardDate.dateRangeEnd]
  );

  const loadTopCustomersBreakup = React.useCallback(
    async (overrideParams = {}) => {
      const fromDate =
        overrideParams.fromDate !== undefined
          ? overrideParams.fromDate
          : topCustomersCardDate.dateRangeStart;
      const toDate =
        overrideParams.toDate !== undefined
          ? overrideParams.toDate
          : topCustomersCardDate.dateRangeEnd;
      const payload = await fetchDashboardPayload(overrideParams, { start: fromDate, end: toDate });
      applyTopCustomersFromPayload(payload);
    },
    [fetchDashboardPayload, topCustomersCardDate.dateRangeStart, topCustomersCardDate.dateRangeEnd]
  );

  const syncChartDateFiltersFromMaster = React.useCallback(
    (master = null) => {
      const source = master ?? {
        dateRange: filters.dateRange,
        dateRangeStart: filters.dateRangeStart,
        dateRangeEnd: filters.dateRangeEnd,
      };
      const cardDate = {
        dateRange: source.dateRange || "",
        dateRangeStart: source.dateRangeStart || "",
        dateRangeEnd: source.dateRangeEnd || "",
      };
      setSrStatusCardDate(cardDate);
      setSrCategoryCardDate(cardDate);
      setTopCustomersCardDate(cardDate);
      [
        srStatusCardDatePickerRef,
        srCategoryCardDatePickerRef,
        topCustomersCardDatePickerRef,
      ].forEach((ref) => {
        if (ref.current) $(ref.current).val(cardDate.dateRange);
      });
      return cardDate;
    },
    [filters.dateRange, filters.dateRangeStart, filters.dateRangeEnd]
  );

  const applyDashboardFilters = React.useCallback(
    (overrideParams = {}) => {
      const seq = ++dashboardFilterSeqRef.current;
      const filterOverride = {
        fromDate: filters.dateRangeStart,
        toDate: filters.dateRangeEnd,
        country: filters.country,
        zone: filters.zone,
        region: filters.region,
        territory: filters.territory,
        bv: bvFilterRef.current,
        status: statusFilterRef.current,
        invoiceNo: filters.invoiceNo,
        ...overrideParams,
      };
      syncChartDateFiltersFromMaster();
      const run = async () => {
        await loadAllDashboardData(filterOverride);
        if (seq !== dashboardFilterSeqRef.current) return;
      };
      run();
    },
    [
      filters.dateRangeStart,
      filters.dateRangeEnd,
      filters.country,
      filters.zone,
      filters.region,
      filters.territory,
      filters.invoiceNo,
      syncChartDateFiltersFromMaster,
      loadAllDashboardData,
    ]
  );

  const handleStatusFilterChange = (selected) => {
    const arr = Array.isArray(selected) ? selected : [];
    statusFilterRef.current = arr;
    setFilters((prev) => ({ ...prev, status: arr }));
  };

  const handleBvFilterChange = (selected) => {
    const arr = Array.isArray(selected) ? selected : [];
    bvFilterRef.current = arr;
    setFilters((prev) => ({ ...prev, bv: arr }));
  };

  loadSrStatusBreakupRef.current = loadSrStatusBreakup;
  loadSrCategoryBreakupRef.current = loadSrCategoryBreakup;
  loadTopCustomersBreakupRef.current = loadTopCustomersBreakup;

  // Chart data configuration (SR Status breakup uses per-card date filter)
  const srStatusValues = [
    srStatusBreakupData.openSRCount,
    srStatusBreakupData.closedSRCount,
    srStatusBreakupData.rejectedSRCount,
    srStatusBreakupData.specialCaseSRCount,
    srStatusBreakupData.approvedPaymentPendingSRCount,
  ];
  const srStatusTotal = srStatusValues.reduce((a, b) => a + (Number(b) || 0), 0);
  const hasSrStatusData = srStatusTotal > 0;

  const chartData = {
    labels: [
      "Open SR Count",
      "Closed SR Count",
      "Rejected SR Count",
      "Special Case SRs",
      "Approved & Payment Pending SR ",
    ],
    datasets: [
      {
        label: "SR Status breakup",
        data: srStatusValues,
        backgroundColor: (ctx) => {
          const v = ctx.dataset.data[ctx.dataIndex];
          const colors = ["#4a90e2", "#50c878", "#ffc107", "#dc3545", "#f97316"];
          return v === 0 ? "transparent" : colors[ctx.dataIndex];
        },
        borderColor: (ctx) => {
          const v = ctx.dataset.data[ctx.dataIndex];
          const colors = ["#4a90e2", "#50c878", "#ffc107", "#dc3545", "#f97316"];
          return v === 0 ? "transparent" : colors[ctx.dataIndex];
        },
        borderWidth: (ctx) => (ctx.dataset.data[ctx.dataIndex] === 0 ? 0 : 1),
      },
    ],
  };

  // When there is no SR status breakup data, show a single ash-colored donut segment with a clear center message.
  const noDataChartData = {
    labels: ["No data"],
    datasets: [
      {
        label: "SR Status breakup",
        data: [1],
        backgroundColor: ["#d6d9dd"],
        borderColor: ["#d6d9dd"],
        borderWidth: 1,
      },
    ],
  };

  // Handle pie chart segment click
  const handlePieChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElementIndex = elements[0].index;

      // Map segment index to status ID
      // 0: Open SR Count -> status 1 (Open)
      // 1: Closed SR Count -> status 5 (Closed)
      // 2: Rejected SR Count -> status 4 (Rejected)
      // 3: Special Case SRs -> rejected then continued
      // 4: Approved & Payment Pending SR  -> status 7 (Payment Pending)
      const statusMap = {
        0: 1, // Open
        1: 5, // Closed
        2: 4, // Rejected
        3: null, // Special Case — handled via specialCase flag
        4: 7, // Approved, Payment Pending
      };

      // Preserve global filters + clicked context (status bucket)
      const clickedStatusNameMap = {
        0: "Pending", // Open bucket
        1: "Closed",
        2: "Rejected",
        3: null,
        4: "Payment Pending",
      };
      if (clickedElementIndex === 3) {
        navigate(
          buildComplaintsDrilldownQuery({
            specialCase: true,
            dateOverride: {
              fromDate: srStatusCardDate?.dateRangeStart,
              toDate: srStatusCardDate?.dateRangeEnd,
            },
            useDashboardStatusWhenNoToken: false,
          })
        );
        return;
      }
      const clickedStatusName = clickedStatusNameMap[clickedElementIndex];
      navigate(
        buildComplaintsDrilldownQuery({
          statusToken: clickedStatusName,
          dateOverride: {
            fromDate: srStatusCardDate?.dateRangeStart,
            toDate: srStatusCardDate?.dateRangeEnd,
          },
          useDashboardStatusWhenNoToken: false,
        })
      );
    }
  };

  // Chart options (compact layout for export: reduced top padding, donut centered)
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    layout: {
      padding: { top: 4, right: 12, bottom: 4, left: 12 },
    },
    plugins: {
      legend: {
        display: false, // Hide default legend as we'll use custom one
      },
      tooltip: {
        enabled: hasSrStatusData,
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage =
              total && total !== 0 ? ((value / total) * 100).toFixed(1) : "0.0";
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
      pieChartValuePlugin: {
        // Hide arc label for the placeholder segment.
        displayValues: hasSrStatusData ? undefined : [0],
      },
      donutCenterTextPlugin: {
        enabled: !hasSrStatusData,
        textLines: ["No data available", "for this criteria."],
        color: "#6c757d",
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 16,
      },
    },
    cutout: "60%", // Makes it a donut chart
    onClick: hasSrStatusData ? handlePieChartClick : undefined, // Disable click when no data
  };

  // Bar chart data configuration
  const barChartData = {
    labels: srCategoryData.labels,
    datasets: [
      {
        label: "SR Count",
        data: srCategoryData.srCounts,
        backgroundColor: [
          "#4a90e2", // Blue
          "#50c878", // Green
          "#ff9800", // Orange
          "#17a2b8", // Light blue/cyan
        ],
        borderColor: ["#4a90e2", "#50c878", "#ff9800", "#17a2b8"],
        borderWidth: 1,
        // Match bar thickness with other bar charts on this page
        barPercentage: 0.7,
        categoryPercentage: srCategoryData.labels.length <= 1 ? 0.3 : 0.8,
        barThickness: 40,
      },
    ],
  };

  // Handle bar chart click
  const handleBarChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElementIndex = elements[0].index;

      // Get the category name from the clicked bar
      const categoryName = barChartData.labels[clickedElementIndex];
      if (categoryName) {
        navigate(
          buildComplaintsDrilldownQuery({
            category: String(categoryName),
            dateOverride: {
              fromDate: srCategoryCardDate?.dateRangeStart,
              toDate: srCategoryCardDate?.dateRangeEnd,
            },
          })
        );
      }
    }
  };

  // Dynamic Y-axis max for SR Category chart (avoid fixed 500; scale to data)
  const categoryDataValues = srCategoryData.srCounts || [];
  const categoryMaxVal = Math.max(0, ...categoryDataValues);
  const categoryYMax =
    categoryMaxVal === 0
      ? 5
      : Math.ceil(categoryMaxVal * 1.2) <= 10
        ? Math.ceil(categoryMaxVal * 1.2)
        : Math.ceil((categoryMaxVal * 1.2) / 5) * 5;
  const categoryStep = categoryYMax <= 10 ? 1 : categoryYMax <= 50 ? 5 : 10;

  // Bar chart options
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false, // Hide legend
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `Count: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        max: categoryYMax,
        suggestedMax: categoryYMax,
        ticks: {
          stepSize: categoryStep,
          precision: 0,
          maxTicksLimit: 11,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          padding: 8,
          font: { size: 11, weight: "600" },
          crossAlign: "near",
          callback: function (value, index) {
            const label = srCategoryData.labels?.[index] ?? value;
            return wrapTickLabel(label, 14, 2);
          },
        },
        grid: {
          display: false,
        },
      },
    },
    onClick: handleBarChartClick, // Add click handler
  };

  // Top 10 customers bar chart data configuration
  // barThickness caps bar width so 1 customer doesn't occupy full chart width
  const topCustomersChartData = {
    labels: topCustomersData.customers,
    datasets: [
      {
        label: "SR Count",
        data: topCustomersData.srCounts,
        backgroundColor: [
          "#4a90e2", // Blue
          "#50c878", // Green
          "#ff9800", // Orange
          "#17a2b8", // Light blue/cyan
          "#9c27b0", // Purple
          "#f44336", // Red
          "#00bcd4", // Cyan
          "#ffeb3b", // Yellow
          "#795548", // Brown
          "#607d8b", // Blue grey
        ],
        borderColor: [
          "#4a90e2",
          "#50c878",
          "#ff9800",
          "#17a2b8",
          "#9c27b0",
          "#f44336",
          "#00bcd4",
          "#ffeb3b",
          "#795548",
          "#607d8b",
        ],
        borderWidth: 1,
        barPercentage: 0.7,
        categoryPercentage: topCustomersData.customers.length <= 1 ? 0.3 : 0.8,
        barThickness: 40, // Fixed max width so few bars don't stretch across full chart
      },
    ],
  };

  // Handle top customers bar chart click
  const handleTopCustomersChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElementIndex = elements[0].index;

      // Get the customer name from the clicked bar
      const customerName = topCustomersChartData.labels[clickedElementIndex];
      if (customerName) {
        const code =
          Array.isArray(topCustomersData.customerCodes) &&
          topCustomersData.customerCodes.length > clickedElementIndex
            ? topCustomersData.customerCodes[clickedElementIndex]
            : "";
        navigate(
          buildComplaintsDrilldownQuery({
            customerCode: code && String(code).trim() ? String(code).trim() : undefined,
            customerName,
            dateOverride: {
              fromDate: topCustomersCardDate?.dateRangeStart,
              toDate: topCustomersCardDate?.dateRangeEnd,
            },
          })
        );
      }
    }
  };

  // Dynamic Y-axis for Top 10 Customers (scale to data, handle empty)
  const customersMaxVal =
    topCustomersData.srCounts.length > 0
      ? Math.max(...topCustomersData.srCounts)
      : 0;
  const customersYMax =
    customersMaxVal === 0
      ? 5
      : customersMaxVal <= 10
        ? Math.ceil(customersMaxVal * 1.2)
        : Math.ceil((customersMaxVal * 1.2) / 5) * 5;

  // Top customers bar chart options
  const topCustomersChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false, // Hide legend
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `SR Count: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        max: customersYMax,
        suggestedMax: customersYMax,
        ticks: {
          precision: 0,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          padding: 8,
          font: { size: 11, weight: "600" },
          crossAlign: "near",
          callback: function (value, index) {
            const label = topCustomersData.customers?.[index] ?? value;
            return wrapTickLabel(label, 14, 2);
          },
        },
        grid: {
          display: false,
        },
      },
    },
    onClick: handleTopCustomersChartClick, // Add click handler
  };

  // Load Status options from Greenlam.SRSTATUSMASTER (active only)
  const loadStatusOptions = async () => {
    try {
      const response = await seDashboardAPI.getSRStatuses(true);
      const ok = response?.success === true || response?.Success === true;
      const list = response?.data ?? response?.Data;
      if (ok && Array.isArray(list)) {
        const options = list.map((s) => ({
          // Backend uses System.Text.Json camelCase, which converts `SRStatusCode` -> `sRStatusCode`
          id: s.srStatusCode || s.sRStatusCode || s.SRStatusCode || "",
          text: s.name || s.Name || "",
        }));
        setStatusOptions(options);
      }
    } catch (e) {
      console.error("Error loading status options:", e);
    }
  };

  const pickDefaultDomesticMarket = (options) => {
    if (lockedMarketCode) return lockedMarketCode;
    if (!Array.isArray(options) || !options.length) return "";
    const domestic =
      options.find((o) => String(o.text || "").trim().toLowerCase() === "domestic") ||
      options.find((o) => String(o.id || "").trim().toUpperCase() === "M001") ||
      options[0];
    return domestic?.id || "";
  };

  // Keep market fixed when user is assigned M001 or M002 only
  useEffect(() => {
    if (!lockedMarketCode) return;
    setFilters((prev) =>
      prev.market === lockedMarketCode
        ? prev
        : {
            ...prev,
            market: lockedMarketCode,
            bv: [],
            country: [],
            zone: [],
            region: [],
            territory: [],
          }
    );
    bvFilterRef.current = [];
  }, [lockedMarketCode]);

  const loadMarketOptions = async () => {
    try {
      const response = await masterDataAPI.getMarkets("", 1, 500);
      const successFlag = response?.success ?? response?.Success;
      const payload = response?.data ?? response?.Data;
      const markets = payload?.markets || payload?.Markets || payload || [];
      const options = (Array.isArray(markets) ? markets : [])
        .filter((m) => m.isActive !== false && m.IsActive !== false)
        .map((m) => ({
          id: m.marketCode || m.MARKETCODE || m.MarketCode,
          text: m.marketName || m.MARKETNAME || m.MarketName || m.marketCode,
        }))
        .filter((o) => o.id);
      setMarketOptions(options);
      setFilters((prev) => {
        const nextMarket =
          lockedMarketCode || prev.market || pickDefaultDomesticMarket(options);
        if (prev.market === nextMarket) return prev;
        return { ...prev, market: nextMarket };
      });
    } catch (e) {
      console.error("Error loading Market options:", e);
    }
  };

  // Load Business Vertical options from Greenlam.CATEGORYMASTER (active categories)
  const loadProductVerticalOptions = async (marketCode) => {
    try {
      const response = await seDashboardAPI.getProductVerticalOptions({
        isActive: true,
        marketCode: marketCode || undefined,
        pageNumber: 1,
        pageSize: 500,
      });
      const successFlag = response?.success ?? response?.Success;
      const payload = response?.data ?? response?.Data;
      if (successFlag && payload) {
        const list = payload.categories ?? payload.Categories ?? [];
        const options = list.map((c) => ({
          id: c.catgCode || c.CatgCode,
          text: c.catgName || c.CatgName || c.catgCode || c.CatgCode,
        }));
        setBvOptions(options);
      }
    } catch (e) {
      console.error("Error loading Business Vertical (category) options:", e);
    }
  };

  // Load Country options — same masterdata API as /csrp/users/add (scoped to logged-in user)
  const loadCountryOptions = async () => {
    await runGeoDropdownLoad("country", async (isCurrent) => {
      try {
        const response = await masterDataAPI.getCountries();
        if (!isCurrent()) return;
        const { ok, payload } = parseApiEnvelope(response);
        const raw = ok && Array.isArray(payload) ? payload : [];
        const loginUserNow = getLoggedInUserFromStorage();
        const scoped = filterMasterCountriesForDashboardUser(
          raw,
          loginUserNow,
          userGeographyRef.current
        );
        const options = withGeoAllOption(scoped, "country");
        if (!isCurrent()) return;
        setCountryOptions(options);

        if (
          loginUserNow &&
          !isSeDashboardAllCountriesUser(loginUserNow) &&
          !isUserGeoAllScopeForDashboard(loginUserNow, "country") &&
          options.filter((o) => !isGeoAllSelectionToken(o.id)).length === 1
        ) {
          const onlyCountry = options.find((o) => !isGeoAllSelectionToken(o.id))?.id;
          if (onlyCountry) {
            setFilters((prev) =>
              prev.country?.length === 1 && prev.country[0] === onlyCountry
                ? prev
                : { ...prev, country: [onlyCountry], zone: [], region: [], territory: [] }
            );
          }
        }
      } catch (e) {
        console.error("Error loading Country options:", e);
        if (!isCurrent()) return;
        setCountryOptions([GEO_ALL_OPTION]);
      }
    });
  };

  // Load Zone options — Export: group by name; Domestic: by country
  const loadZoneOptions = async (countryCodes) => {
    await runGeoDropdownLoad("zone", async (isCurrent) => {
      try {
        const merged = [];
        const codes = Array.isArray(countryCodes)
          ? countryCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!codes.length) {
          const res = await territoryMasterAPI.getZones();
          if (!isCurrent()) return;
          const raw = extractZonesFromResponse(res);
          const active = Array.isArray(raw)
            ? raw.filter((z) => z.isActive !== false && z.IsActive !== false)
            : [];
          active.forEach((z) =>
            merged.push(normalizeZoneRow(z, z.countryCode || z.CountryCode))
          );
        } else {
          const zoneResults = await Promise.all(
            codes.map(async (cc) => {
              const res = await territoryMasterAPI.getZones(cc);
              const raw = extractZonesFromResponse(res);
              const active = Array.isArray(raw)
                ? raw.filter((z) => z.isActive !== false && z.IsActive !== false)
                : [];
              return active.map((z) => normalizeZoneRow(z, cc));
            })
          );
          if (!isCurrent()) return;
          zoneResults.flat().forEach((z) => merged.push(z));
        }
        const activeRows = merged.filter(
          (z) => z.zoneCode && !isGeoAllOptionRow(z.zoneCode, z.zoneName)
        );
        const exportMode = isExportCascadeRef.current;
        let mapped = exportMode
          ? mapExportZonesToOptions(activeRows, countryOptions)
          : (() => {
              const byZone = new Map();
              activeRows.forEach((z) => {
                byZone.set(z.zoneCode, mapZoneToOption(z, countryOptions));
              });
              return [...byZone.values()];
            })();
        mapped = filterGeoOptionsForDashboardUser(
          mapped,
          loginUser,
          "zone",
          userGeographyRef.current
        );
        if (!isCurrent()) return;
        setZoneOptions(withGeoAllOption(mapped, "zone"));
      } catch (e) {
        console.error("Error loading Zone options:", e);
        if (!isCurrent()) return;
        setZoneOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadRegionOptions = async (zoneCodes, countryCodes, zonesList, exportMode) => {
    await runGeoDropdownLoad("region", async (isCurrent) => {
      try {
        const zSel = Array.isArray(zoneCodes)
          ? zoneCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        const cSel = Array.isArray(countryCodes)
          ? countryCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!zSel.length || (exportMode && !cSel.length)) {
          if (!isCurrent()) return;
          regionOptionsRef.current = [GEO_ALL_OPTION];
          setRegionOptions([GEO_ALL_OPTION]);
          return;
        }
        const regionJobs = [];
        for (const zoneOptionId of zSel) {
          const zrow = findZoneOption(zonesList, zoneOptionId);
          const linkedCountries = Array.isArray(zrow?.countryCodes)
            ? zrow.countryCodes.map((c) => String(c).trim()).filter(Boolean)
            : zrow?.countryCode
              ? [String(zrow.countryCode).trim()]
              : [];
          const countriesToQuery = exportMode
            ? cSel.filter(
                (cc) =>
                  !linkedCountries.length || linkedCountries.includes(String(cc).trim())
              )
            : cSel.length
              ? cSel.filter(
                  (cc) =>
                    !linkedCountries.length ||
                    linkedCountries.includes(String(cc).trim())
                )
              : linkedCountries.length
                ? linkedCountries
                : [];
          for (const cc of countriesToQuery) {
            if (!cc || isGeoAllSelectionToken(cc)) continue;
            const zoneCode =
              resolveZoneCodeForCountry(zoneOptionId, cc, zonesList) || zoneOptionId;
            if (!zoneCode) continue;
            regionJobs.push(
              (async () => {
                const res = await territoryMasterAPI.getRegions(cc, zoneCode);
                const raw = extractRegionsFromResponse(res);
                const active = Array.isArray(raw)
                  ? raw.filter((r) => r.isActive !== false && r.IsActive !== false)
                  : [];
                return active;
              })()
            );
          }
        }
        const regionResults = await Promise.all(regionJobs);
        if (!isCurrent()) return;
        const merged = [];
        regionResults.forEach((active) => {
          active.forEach((r) => merged.push(normalizeRegionRow(r)));
        });
        const byReg = new Map();
        merged.forEach((r) => {
          if (!r.regionCode || isGeoAllOptionRow(r.regionCode, r.regionName)) return;
          byReg.set(r.regionCode, mapRegionToOption(r));
        });
        const scoped = filterGeoOptionsForDashboardUser(
          [...byReg.values()],
          loginUser,
          "region",
          userGeographyRef.current
        );
        if (!isCurrent()) return;
        const options = withGeoAllOption(scoped, "region");
        regionOptionsRef.current = options;
        setRegionOptions(options);
      } catch (e) {
        console.error("Error loading Region options:", e);
        if (!isCurrent()) return;
        regionOptionsRef.current = [GEO_ALL_OPTION];
        setRegionOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadTerritoryOptions = async (
    regionCodes,
    zonesList,
    regionsList,
    filterZoneCodes,
    filterCountryCodes
  ) => {
    await runGeoDropdownLoad("territory", async (isCurrent) => {
      try {
        const rSel = Array.isArray(regionCodes)
          ? regionCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!rSel.length) {
          if (!isCurrent()) return;
          setTerritoryOptions([GEO_ALL_OPTION]);
          return;
        }

        const regionsListResolved =
          (Array.isArray(regionsList) && regionsList.length
            ? regionsList
            : regionOptionsRef.current) || [];
        const zonesListResolved =
          (Array.isArray(zonesList) && zonesList.length
            ? zonesList
            : zoneOptionsRef.current) || [];

        const territoryJobs = [];
        for (const rc of rSel) {
          const row = regionsListResolved.find((x) => x.id === rc);
          const pairs = resolveRegionGeoCandidates(
            rc,
            row,
            regionsListResolved,
            zonesListResolved,
            filterZoneCodes,
            filterCountryCodes
          );
          if (!pairs.length) continue;

          for (const { cc, zc } of pairs) {
            if (isGeoAllSelectionToken(cc) || isGeoAllSelectionToken(zc)) continue;
            territoryJobs.push(
              (async () => {
                const res = await territoryMasterAPI.getPaged({
                  countryCode: cc,
                  zoneCode: zc,
                  regionCode: rc,
                  status: "active",
                  pageNumber: 1,
                  pageSize: 5000,
                });
                const { ok, payload } = parseApiEnvelope(res);
                const items = ok && payload
                  ? payload.territories || payload.Territories || []
                  : [];
                return Array.isArray(items)
                  ? items.filter((t) => t.isActive !== false && t.IsActive !== false)
                  : [];
              })()
            );
          }
        }
        const territoryResults = await Promise.all(territoryJobs);
        if (!isCurrent()) return;
        const merged = [];
        territoryResults.forEach((active) => {
          active.forEach((t) => merged.push(normalizeTerritoryRow(t)));
        });
        const byT = new Map();
        merged.forEach((t) => {
          if (!t.territoryCode || isGeoAllOptionRow(t.territoryCode, t.territoryName)) {
            return;
          }
          byT.set(t.territoryCode, mapTerritoryToOption(t));
        });
        const scoped = filterGeoOptionsForDashboardUser(
          [...byT.values()],
          loginUser,
          "territory",
          userGeographyRef.current
        );
        if (!isCurrent()) return;
        const options = withGeoAllOption(scoped, "territory");
        setTerritoryOptions(options);

        if (scoped.filter((o) => !isGeoAllSelectionToken(o.id)).length === 1) {
          const only = scoped.find((o) => !isGeoAllSelectionToken(o.id))?.id;
          if (only) {
            setFilters((prev) =>
              prev.territory?.length === 1 && prev.territory[0] === only
                ? prev
                : { ...prev, territory: [only] }
            );
          }
        }
      } catch (e) {
        console.error("Error loading Territory options:", e);
        if (!isCurrent()) return;
        setTerritoryOptions([GEO_ALL_OPTION]);
      }
    });
  };

  // Load invoice suggestions (mock - replace with actual API)
  const loadInvoiceSuggestions = async (searchText) => {
    if (searchText.length < 4) {
      setInvoiceSuggestions([]);
      setShowInvoiceSuggestions(false);
      return;
    }
    // TODO: Replace with actual API call
    // For now, mock suggestions
    setInvoiceSuggestions([
      searchText + "001",
      searchText + "002",
      searchText + "003",
    ]);
    setShowInvoiceSuggestions(true);
  };

  // Handle invoice input change
  const handleInvoiceInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only digits
    setFilters((prev) => ({ ...prev, invoiceNo: value }));
    if (value.length >= 4) {
      loadInvoiceSuggestions(value);
    } else {
      setShowInvoiceSuggestions(false);
    }
  };

  // Handle invoice suggestion click
  const handleInvoiceSuggestionClick = (suggestion) => {
    setFilters((prev) => ({ ...prev, invoiceNo: suggestion }));
    setShowInvoiceSuggestions(false);
  };

  // Close invoice suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showInvoiceSuggestions &&
        !event.target.closest(".invoice-suggestions-list") &&
        !event.target.closest('input[placeholder="Enter invoice number"]')
      ) {
        setShowInvoiceSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showInvoiceSuggestions]);

  const handleFilter = () => {
    applyDashboardFilters();
  };

  // Handle reset button click
  const handleReset = () => {
    // Reset all filters to defaults (current Financial Year, all statuses / product verticals / countries)
    const fy = getCurrentFinancialYearRange();
    const defaultMarket = pickDefaultDomesticMarket(marketOptions);
    setFilters({
      dateRange: fy.dateRange,
      dateRangeStart: fy.dateRangeStart,
      dateRangeEnd: fy.dateRangeEnd,
      status: [],
      bv: [],
      invoiceNo: "",
      market: defaultMarket,
      country: [],
      zone: [],
      region: [],
      territory: [],
    });
    statusFilterRef.current = [];
    bvFilterRef.current = [];
    setDateRangeText(fy.dateRange);
    setStartDate(fy.dateRangeStart);
    setEndDate(fy.dateRangeEnd);

    // Clear date range picker and set to default FY display
    if (filterDateRangePickerRef.current) {
      $(filterDateRangePickerRef.current).val(fy.dateRange);
    }

    // Clear invoice suggestions
    setShowInvoiceSuggestions(false);
    setInvoiceSuggestions([]);

    syncChartDateFiltersFromMaster({
      dateRange: fy.dateRange,
      dateRangeStart: fy.dateRangeStart,
      dateRangeEnd: fy.dateRangeEnd,
    });

    const resetOverride = {
      fromDate: fy.dateRangeStart,
      toDate: fy.dateRangeEnd,
      status: [],
      bv: [],
      country: [],
      zone: [],
      region: [],
      territory: [],
      invoiceNo: "",
    };
    applyDashboardFilters(resetOverride);
  };

  // Load initial data: Status from SRSTATUSMASTER, Business Vertical from CATEGORYMASTER, default date = current FY
  useEffect(() => {
    const init = async () => {
      await loadUserGeography();
      loadStatusOptions();
      loadMarketOptions();
      loadCountryOptions();
      loadAllDashboardData();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-scope geo dropdowns when UserLocationMapping geography loads / updates
  useEffect(() => {
    if (!userGeography) return;
    loadCountryOptions();
    if (isExportCascade) {
      loadZoneOptions();
    } else if (filters.country?.length) {
      loadZoneOptions(filters.country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGeography]);

  // Per-chart KPI date range pickers (independent from global filter date)
  useEffect(() => {
    const cleanups = [
      initDashboardCardDateRangePicker(srStatusCardDatePickerRef.current, {
        initialDisplay: srStatusCardDate.dateRange,
        onApply: ({ start, end, display }) => {
          setSrStatusCardDate({
            dateRange: display,
            dateRangeStart: start,
            dateRangeEnd: end,
          });
          loadSrStatusBreakupRef.current?.({ fromDate: start, toDate: end });
        },
      }),
      initDashboardCardDateRangePicker(srCategoryCardDatePickerRef.current, {
        initialDisplay: srCategoryCardDate.dateRange,
        onApply: ({ start, end, display }) => {
          setSrCategoryCardDate({
            dateRange: display,
            dateRangeStart: start,
            dateRangeEnd: end,
          });
          loadSrCategoryBreakupRef.current?.({ fromDate: start, toDate: end });
        },
      }),
      initDashboardCardDateRangePicker(topCustomersCardDatePickerRef.current, {
        initialDisplay: topCustomersCardDate.dateRange,
        onApply: ({ start, end, display }) => {
          setTopCustomersCardDate({
            dateRange: display,
            dateRangeStart: start,
            dateRangeEnd: end,
          });
          loadTopCustomersBreakupRef.current?.({ fromDate: start, toDate: end });
        },
      }),
    ];
    return () => cleanups.forEach((fn) => fn?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Master filter date: sync all KPI chart date pickers and reload cards + charts
  useEffect(() => {
    if (skipDateFilterDashboardLoadRef.current) {
      skipDateFilterDashboardLoadRef.current = false;
      return;
    }
    if (filters.dateRangeStart || filters.dateRangeEnd) {
      syncChartDateFiltersFromMaster();
      loadAllDashboardData({
        fromDate: filters.dateRangeStart,
        toDate: filters.dateRangeEnd,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.dateRangeStart, filters.dateRangeEnd]);

  // Business Vertical scoped to selected Market
  useEffect(() => {
    if (!filters.market) {
      setBvOptions([]);
      return;
    }
    loadProductVerticalOptions(filters.market);
    bvFilterRef.current = [];
    setFilters((prev) => ({ ...prev, bv: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.market]);

  // Export: load all zones when market is Export
  useEffect(() => {
    if (!isExportCascade) return;
    loadZoneOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade]);

  // Domestic: load zones when country changes (skip when Country = All)
  useEffect(() => {
    if (isExportCascade) return;
    if (isGeoAllFilterValue(filters.country)) {
      setZoneOptions([GEO_ALL_OPTION]);
      setRegionOptions([GEO_ALL_OPTION]);
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    if (filters.country?.length) {
      loadZoneOptions(filters.country);
    } else {
      setZoneOptions([]);
      setFilters((prev) => ({ ...prev, zone: [], region: [], territory: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade, filters.country]);

  // Clear geo when switching Export ↔ Domestic (skip initial mount)
  useEffect(() => {
    if (prevExportCascadeRef.current === null) {
      prevExportCascadeRef.current = isExportCascade;
      return;
    }
    if (prevExportCascadeRef.current === isExportCascade) return;
    prevExportCascadeRef.current = isExportCascade;

    setZoneOptions([]);
    setRegionOptions([]);
    setTerritoryOptions([]);
    setFilters((prev) => ({
      ...prev,
      country: [],
      zone: [],
      region: [],
      territory: [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade]);

  // Refresh zone labels when country names load (Domestic: Zone (Country); Export: zone name only)
  useEffect(() => {
    if (!zoneOptions.length || !countryOptions.length) return;
    if (isGeoAllFilterValue(filters.zone)) return;
    setZoneOptions((prev) =>
      prev.map((z) => {
        if (isGeoAllSelectionToken(z.id) || isGeoAllOptionRow(z.id, z.text)) return z;
        return {
          ...z,
          text: formatZoneOptionLabel(z, countryOptions, {
            includeCountry: !isExportCascade,
          }),
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryOptions, isExportCascade]);

  // Regions: require zone (+ country for Export); skip when parent is All
  useEffect(() => {
    if (isGeoAllFilterValue(filters.zone) || isGeoAllFilterValue(filters.country)) {
      setRegionOptions([GEO_ALL_OPTION]);
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    const zOk = filters.zone?.length;
    const cOk = !isExportCascade || filters.country?.length;
    if (zOk && cOk) {
      loadRegionOptions(
        filters.zone,
        filters.country,
        zoneOptionsRef.current.length ? zoneOptionsRef.current : zoneOptions,
        isExportCascade
      );
    } else {
      setRegionOptions([]);
      setFilters((prev) => ({ ...prev, region: [], territory: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.zone, filters.country, isExportCascade, zoneOptionIdsKey]);

  // Territories for selected region(s) — wait until region rows include country/zone parents
  useEffect(() => {
    if (
      isGeoAllFilterValue(filters.region) ||
      isGeoAllFilterValue(filters.zone) ||
      isGeoAllFilterValue(filters.country)
    ) {
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    if (!filters.region?.length) {
      setTerritoryOptions([]);
      setFilters((prev) => ({ ...prev, territory: [] }));
      return;
    }
    const regionsReady =
      regionOptions.length > 0 || regionOptionsRef.current.length > 0;
    if (!regionsReady) return;

    loadTerritoryOptions(
      filters.region,
      zoneOptions.length ? zoneOptions : zoneOptionsRef.current,
      regionOptions.length ? regionOptions : regionOptionsRef.current,
      filters.zone,
      filters.country
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.region, filters.zone, filters.country, regionOptions, zoneOptionIdsKey]);

  // User scope ALL → select sentinel All (0000), same as reports / admin dashboard
  // Export (M002): zone is not auto-selected; user picks zone(s) first.
  useEffect(() => {
    if (isExportCascade) return;
    if (!isUserGeoAllScopeForDashboard(loginUser, "zone")) return;
    if (!filters.country?.length || !zoneOptions.length) return;
    const allVal = [USER_DEFAULT_GEO_CODE];
    setFilters((prev) => {
      if (geoFilterArraysEqual(prev.zone, allVal)) return prev;
      return { ...prev, zone: allVal, region: allVal, territory: allVal };
    });
  }, [loginUser, zoneOptions, isExportCascade, filters.country]);

  useEffect(() => {
    if (!isUserGeoAllScopeForDashboard(loginUser, "country")) return;
    const list = isExportCascade ? exportCountryOptions : countryOptions;
    const parentOk = !isExportCascade || filters.zone?.length;
    if (!parentOk || !list.length) return;
    const allVal = [USER_DEFAULT_GEO_CODE];
    setFilters((prev) => {
      if (geoFilterArraysEqual(prev.country, allVal)) return prev;
      if (isExportCascade) {
        return { ...prev, country: allVal, region: allVal, territory: allVal };
      }
      return {
        ...prev,
        country: allVal,
        zone: allVal,
        region: allVal,
        territory: allVal,
      };
    });
  }, [
    loginUser,
    countryOptions,
    exportCountryOptions,
    isExportCascade,
    filters.zone,
  ]);

  useEffect(() => {
    if (!isUserGeoAllScopeForDashboard(loginUser, "region")) return;
    const zOk = filters.zone?.length;
    const cOk = !isExportCascade || filters.country?.length;
    if (!zOk || !cOk || !regionOptions.length) return;
    const allVal = [USER_DEFAULT_GEO_CODE];
    setFilters((prev) => {
      if (geoFilterArraysEqual(prev.region, allVal)) return prev;
      return { ...prev, region: allVal, territory: allVal };
    });
  }, [
    loginUser,
    regionOptions,
    isExportCascade,
    filters.zone,
    filters.country,
  ]);

  useEffect(() => {
    if (!isUserGeoAllScopeForDashboard(loginUser, "territory")) return;
    if (!filters.region?.length || !territoryOptions.length) return;
    const allVal = [USER_DEFAULT_GEO_CODE];
    setFilters((prev) => {
      if (geoFilterArraysEqual(prev.territory, allVal)) return prev;
      return { ...prev, territory: allVal };
    });
  }, [loginUser, territoryOptions, filters.region]);

  const getPieChartExportRows = () => {
    const total = chartData.datasets[0].data.reduce((a, b) => a + b, 0);
    const rows = chartData.labels.map((label, index) => {
      const value = chartData.datasets[0].data[index];
      const pct =
        total && total !== 0
          ? `${((value / total) * 100).toFixed(2)}%`
          : "0%";
      return { Category: label, Count: value, Percentage: pct };
    });
    rows.push({ Category: "Total", Count: total, Percentage: "100%" });
    return rows;
  };

  const handlePieChartExportExcel = async () => {
    const rows = getPieChartExportRows();
    await exportJsonToExcel(
      rows,
      `sr-status-breakup-${formatDateDDMMYYYY()}.xlsx`,
      "SR Status breakup"
    );
  };
  const handlePieChartExportCsv = () => {
    exportRowsToCsv(
      getPieChartExportRows(),
      `sr-status-breakup-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handlePieChartExportPdf = () => {
    exportRowsToPdf(
      getPieChartExportRows(),
      `sr-status-breakup-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  // Initialize daterangepicker for top right
  useEffect(() => {
    if (dateRangePickerRef.current) {
      const $input = $(dateRangePickerRef.current);

      $input.daterangepicker({
        autoUpdateInput: false,
        locale: {
          cancelLabel: "Clear",
          applyLabel: "Apply",
          format: "DD-MM-YYYY",
        },
        opens: "left",
        drops: "down",
      });

      $input.on("apply.daterangepicker", function (ev, picker) {
        const start = picker.startDate.format("YYYY-MM-DD");
        const end = picker.endDate.format("YYYY-MM-DD");
        const displayText =
          picker.startDate.format("DD-MM-YYYY") +
          " - " +
          picker.endDate.format("DD-MM-YYYY");

        setStartDate(start);
        setEndDate(end);
        setDateRangeText(displayText);
        $(this).val(displayText);
      });

      $input.on("cancel.daterangepicker", function (ev, picker) {
        setStartDate("");
        setEndDate("");
        setDateRangeText("");
        $(this).val("");
      });

      // Cleanup
      return () => {
        if ($input.data("daterangepicker")) {
          $input.off("apply.daterangepicker");
          $input.off("cancel.daterangepicker");
          $input.data("daterangepicker").remove();
        }
      };
    }
  }, []);

  // Initialize filter date range picker with predefined ranges
  useEffect(() => {
    // Only initialize if filter panel is expanded and input exists
    if (!filterExpanded || !filterDateRangePickerRef.current) return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const $input = $(filterDateRangePickerRef.current);
      if (!$input.length) return;
      
      // Destroy existing instance if any
      if ($input.data("daterangepicker")) {
        $input.data("daterangepicker").remove();
      }

      $input.daterangepicker({
        autoUpdateInput: false,
        locale: {
          cancelLabel: "Clear",
          applyLabel: "Apply",
          format: "DD-MM-YYYY",
        },
        opens: "left",
        drops: "down",
        showDropdowns: true,
        ranges: getDashboardDateRangePresets(),
      });

      if (filters.dateRange) {
        $input.val(filters.dateRange);
      }

      $input.on("apply.daterangepicker", function (ev, picker) {
        const displayText =
          picker.startDate.format("DD-MM-YYYY") +
          " - " +
          picker.endDate.format("DD-MM-YYYY");

        setFilters((prev) => ({
          ...prev,
          dateRange: displayText,
          dateRangeStart: picker.startDate.format("YYYY-MM-DD"),
          dateRangeEnd: picker.endDate.format("YYYY-MM-DD"),
        }));
        $(this).val(displayText);
      });

      $input.on("cancel.daterangepicker", function (ev, picker) {
        setFilters((prev) => ({ ...prev, dateRange: "" }));
        $(this).val("");
      });
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timer);
      if (filterDateRangePickerRef.current) {
        const $input = $(filterDateRangePickerRef.current);
        if ($input.data("daterangepicker")) {
          $input.off("apply.daterangepicker");
          $input.off("cancel.daterangepicker");
          $input.data("daterangepicker").remove();
        }
      }
    };
  }, [filterExpanded]);

  const getBarChartExportRows = () => {
    const rows = barChartData.labels.map((label, index) => ({
      Category: label,
      "SR Count": barChartData.datasets[0].data[index],
    }));
    const total = barChartData.datasets[0].data.reduce((a, b) => a + b, 0);
    rows.push({ Category: "Total", "SR Count": total });
    return rows;
  };

  const handleBarChartExportExcel = async () => {
    await exportJsonToExcel(
      getBarChartExportRows(),
      `complaint-category-breakup-${formatDateDDMMYYYY()}.xlsx`,
      "SR Category breakup"
    );
  };
  const handleBarChartExportCsv = () => {
    exportRowsToCsv(
      getBarChartExportRows(),
      `complaint-category-breakup-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleBarChartExportPdf = () => {
    exportRowsToPdf(
      getBarChartExportRows(),
      `complaint-category-breakup-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "portrait", fontSize: 9 }
    );
  };

  const getTopCustomersExportRows = () => {
    const rows = topCustomersChartData.labels.map((label, index) => ({
      Customer: label,
      "SR Count": topCustomersChartData.datasets[0].data[index],
    }));
    const total = topCustomersChartData.datasets[0].data.reduce((a, b) => a + b, 0);
    rows.push({ Customer: "Total", "SR Count": total });
    return rows;
  };

  const handleTopCustomersExportExcel = async () => {
    await exportJsonToExcel(
      getTopCustomersExportRows(),
      `top-10-customers-by-sr-count-${formatDateDDMMYYYY()}.xlsx`,
      "Top 10 Customers by SR Count"
    );
  };
  const handleTopCustomersExportCsv = () => {
    exportRowsToCsv(
      getTopCustomersExportRows(),
      `top-10-customers-by-sr-count-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleTopCustomersExportPdf = () => {
    exportRowsToPdf(
      getTopCustomersExportRows(),
      `top-10-customers-by-sr-count-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  const renderChartCardDateFilter = (pickerRef, displayText) => {
    const rangeLabel = (displayText || "").trim();
    return (
      <div className="chart-card-date-filter">
        <input
          type="text"
          ref={pickerRef}
          className="form-control form-control-light chart-card-date-input text-start"
          placeholder="Select date range"
          readOnly
          value={rangeLabel}
          style={{ width: `${getChartDateInputWidthPx(rangeLabel)}px` }}
        />
        <CalendarTodayIcon className="chart-card-date-icon" aria-hidden />
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      {/* <h1 className="dashboard-title">Dashboard</h1> */}

      <div className="container-fluid">
        {/* Filter Card */}
        <div className="row mb-3">
          <div className="col-12">
            <div 
              className="card-panel mb-3 filter-panel"
              style={{ paddingBottom: filterExpanded ? "15px" : "2px" }}
            >
              <div
                className={`section-heading d-flex align-items-center gap-2 ${
                  filterExpanded ? "mb-3" : "filter-panel-heading-collapsed"
                }`}
                style={{
                  marginBottom: filterExpanded ? "14px" : 0,
                  cursor: "pointer",
                }}
                onClick={() => setFilterExpanded(!filterExpanded)}
              >
                <FilterListIcon
                  fontSize="small"
                  className="text-muted"
                  style={{
                    width: "18px",
                    height: "18px",
                    flexShrink: 0,
                    marginTop: "2px",
                  }}
                />
                <span>Filter</span>
                <span className="filter-panel-active-daterange ms-auto">
                  {filters.dateRange || defaultFY.dateRange || "Select date range"}
                </span>
                {filterExpanded ? (
                  <ExpandLessIcon
                    fontSize="small"
                    className="text-muted"
                    style={{
                      width: "18px",
                      height: "18px",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    className="text-muted"
                    style={{
                      width: "18px",
                      height: "18px",
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>

              {filterExpanded && (
              <div className="container-fluid">
                <div className="row g-3 align-items-end">
                  {/* Date Range */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">
                      Select Date Range
                    </label>
                    <div
                      className="input-icon"
                      style={{ position: "relative" }}
                    >
                      <input
                        type="text"
                        ref={filterDateRangePickerRef}
                        className="form-control form-control-light text-start"
                        placeholder="Select date range"
                        readOnly
                        style={{ height: "42px", textAlign: "left" }}
                      />
                      <CalendarTodayIcon
                        className="icon-right"
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          width: "16px",
                          height: "16px",
                          color: "#6c757d",
                        }}
                      />
                    </div>
                  </div>

                  {/* Status Dropdown - same structure as Product Category on SLA-TAT */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">
                      Status
                    </label>
                    <select
                      ref={statusSelectRef}
                      className="form-select form-control-light filter-select text-start"
                      style={{ height: "42px", textAlign: "left", display: "none" }}
                      multiple
                    >
                      {statusOptions.length > 0 ? (
                        statusOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.text}
                          </option>
                        ))
                      ) : null}
                    </select>
                    <CustomMultiSelect
                      options={statusOptions}
                      value={filters.status || []}
                      onChange={handleStatusFilterChange}
                      placeholder="Select Status"
                      disabled={false}
                    />
                  </div>

                  {/* Business Vertical Dropdown - same structure as Product Category on SLA-TAT */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">
                      Business Vertical
                    </label>
                    <select
                      ref={bvSelectRef}
                      className="form-select form-control-light filter-select text-start"
                      style={{ height: "42px", textAlign: "left", display: "none" }}
                    >
                      {bvOptions.length > 0 ? (
                        bvOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.text}
                          </option>
                        ))
                      ) : null}
                    </select>
                    <CustomMultiSelect
                      options={bvOptions}
                      value={filters.bv || []}
                      onChange={handleBvFilterChange}
                      placeholder="Select Business Vertical"
                      disabled={false}
                    />
                  </div>

                  {/* Invoice No. Input */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">
                      Invoice No.
                    </label>
                    <div
                      className="input-icon"
                      style={{ position: "relative" }}
                    >
                      <input
                        type="text"
                        className="form-control form-control-light text-start"
                        placeholder="Enter invoice number"
                        value={filters.invoiceNo}
                        onChange={handleInvoiceInputChange}
                        style={{ height: "42px", textAlign: "left" }}
                      />
                      <SearchIcon
                        className="icon-right"
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          width: "16px",
                          height: "16px",
                          color: "#6c757d",
                        }}
                      />
                      {showInvoiceSuggestions &&
                        invoiceSuggestions.length > 0 && (
                          <ul
                            className="invoice-suggestions-list"
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              backgroundColor: "white",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              marginTop: "4px",
                              maxHeight: "200px",
                              overflowY: "auto",
                              zIndex: 1000,
                              listStyle: "none",
                              padding: 0,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            }}
                          >
                            {invoiceSuggestions.map((suggestion, index) => (
                              <li
                                key={index}
                                onClick={() =>
                                  handleInvoiceSuggestionClick(suggestion)
                                }
                                style={{
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #eee",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = "#f5f5f5";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = "white";
                                }}
                              >
                                {suggestion}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </div>

                  {(isExportCascade
                    ? ["zone", "country", "region", "territory"]
                    : ["country", "zone", "region", "territory"]
                  ).map((geoKey) => {
                    const countryIsAll = isGeoAllFilterValue(filters.country);
                    const zoneIsAll = isGeoAllFilterValue(filters.zone);
                    const regionIsAll = isGeoAllFilterValue(filters.region);
                    if (geoKey === "country") {
                      const countryList = isExportCascade
                        ? exportCountryOptions
                        : countryOptions;
                      return (
                        <div key="country" className="col-12 col-md-6 col-lg-3">
                          <label className="input-label d-block mb-1 text-start">
                            Country
                          </label>
                          <CustomMultiSelect
                            options={countryList}
                            value={filters.country || []}
                            onChange={(selected) =>
                              handleGeoFilterChange("country", selected, countryList)
                            }
                            placeholder={
                              isExportCascade
                                ? filters.zone?.length
                                  ? "Select Country"
                                  : "Select zone first"
                                : "Select Country"
                            }
                            disabled={
                              !filters.market ||
                              (isExportCascade &&
                                (!filters.zone?.length || zoneIsAll))
                            }
                            loading={geoLoading.country}
                            loadingText="Loading countries..."
                            showSelectAll={!countryIsAll && !zoneIsAll}
                          />
                        </div>
                      );
                    }
                    if (geoKey === "zone") {
                      return (
                        <div key="zone" className="col-12 col-md-6 col-lg-3">
                          <label className="input-label d-block mb-1 text-start">
                            Zone
                          </label>
                          <CustomMultiSelect
                            options={zoneOptions}
                            value={filters.zone || []}
                            onChange={(selected) =>
                              handleGeoFilterChange("zone", selected, zoneOptions)
                            }
                            placeholder={
                              !filters.market
                                ? "Select market first"
                                : isExportCascade
                                  ? "Select zone(s)"
                                  : filters.country?.length
                                    ? "Select zone(s)"
                                    : "Select country first"
                            }
                            disabled={
                              !filters.market ||
                              (!isExportCascade &&
                                (!filters.country?.length || countryIsAll))
                            }
                            loading={geoLoading.zone}
                            loadingText="Loading zones..."
                            showSelectAll={!zoneIsAll && !countryIsAll}
                          />
                        </div>
                      );
                    }
                    if (geoKey === "region") {
                      const regionReady = isExportCascade
                        ? filters.zone?.length && filters.country?.length
                        : filters.zone?.length;
                      const lockedByAll = countryIsAll || zoneIsAll || regionIsAll;
                      return (
                        <div key="region" className="col-12 col-md-6 col-lg-3">
                          <label className="input-label d-block mb-1 text-start">
                            Region
                          </label>
                          <CustomMultiSelect
                            options={regionOptions}
                            value={filters.region || []}
                            onChange={(selected) =>
                              handleGeoFilterChange("region", selected, regionOptions)
                            }
                            placeholder={
                              regionReady
                                ? "Select region(s)"
                                : isExportCascade
                                  ? "Select country first"
                                  : "Select zone first"
                            }
                            disabled={!filters.market || !regionReady || lockedByAll}
                            loading={geoLoading.region}
                            loadingText="Loading regions..."
                            showSelectAll={!lockedByAll}
                          />
                        </div>
                      );
                    }
                    const territoryLockedByAll =
                      countryIsAll || zoneIsAll || regionIsAll;
                    return (
                      <div key="territory" className="col-12 col-md-6 col-lg-3">
                        <label className="input-label d-block mb-1 text-start">
                          Territory
                        </label>
                        <CustomMultiSelect
                          options={territoryOptions}
                          value={filters.territory || []}
                          onChange={(selected) =>
                            handleGeoFilterChange(
                              "territory",
                              selected,
                              territoryOptions
                            )
                          }
                          placeholder={
                            filters.region?.length
                              ? "Select territory(ies)"
                              : "Select region first"
                          }
                          disabled={
                            !filters.market ||
                            !filters.region?.length ||
                            territoryLockedByAll
                          }
                          loading={geoLoading.territory}
                          loadingText="Loading territories..."
                          showSelectAll={!territoryLockedByAll}
                          selectAllLabel={
                            territoryOptions.length > 0
                              ? `Select all territories (${territoryOptions.length})`
                              : "Select all territories"
                          }
                        />
                      </div>
                    );
                  })}

                  {/* Action Buttons */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label
                      className="input-label d-block mb-1"
                      style={{ visibility: "hidden" }}
                    >
                      Actions
                    </label>
                    <div className="d-flex gap-2 filter-buttons">
                      <button
                        type="button"
                        className="btn btn-filter "
                        onClick={handleFilter}
                        style={{
                          background:
                            "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                          border: "none",
                          color: "white",
                          fontWeight: 600,
                          boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                          borderRadius: "4px",
                          padding: "8px 24px",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(102, 126, 234, 0.6)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 15px rgba(102, 126, 234, 0.4)";
                        }}
                        onMouseDown={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                        onMouseUp={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                      >
                        <FilterListIcon fontSize="small" className="me-1" />
                        Filter
                      </button>
                      <button
                        type="button"
                        className="btn btn-reset-filter"
                        onClick={handleReset}
                        title="Reset"
                        style={{
                          background:
                            "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                          border: "none",
                          color: "white",
                          fontWeight: 600,
                          boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                          borderRadius: "4px",
                          padding: "8px 16px",
                          transition: "all 0.3s ease",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(132, 250, 176, 0.6)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 15px rgba(132, 250, 176, 0.4)";
                        }}
                        onMouseDown={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                        onMouseUp={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                      >
                        <RestartAltIcon fontSize="small" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Date Range Filter - Top Right */}
        {/* <div className="row mb-3">
          <div className="col-12">
            <div className="dashboard-filter-container">
              <div className="date-range-picker-wrapper dshbrdDtpkrWrpr">
                <label className="filter-label">Filter by</label>
                <div
                  className="input-icon filter-daterange-wrapper filter-daterangepickerWrpr"
                  style={{ position: "relative" }}
                >
                  <input
                    type="text"
                    ref={dateRangePickerRef}
                    className="form-control form-control-light filter-daterangepicker"
                    placeholder="Select date range"
                    readOnly
                  />
                  <CalendarTodayIcon
                    className="icon-right"
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      width: "16px",
                      height: "16px",
                      color: "#6c757d",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div> */}
        {/* SR Count Cards Row */}
        <div className="row">
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card total"
              onClick={() => navigate(buildComplaintsDrilldownQuery())}
            >
              <div className="sr-count-label">Total SR Count</div>
              <div className="sr-count-subtitle">
                Total SR&apos;s visible
              </div>
              <div className="sr-count-value">{totalSRCount}</div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card open"
              onClick={() =>
                navigate(buildComplaintsDrilldownQuery({ statusToken: 1 }))
              }
            >
              <div className="sr-count-label">Open SR Count</div>
              <div className="sr-count-subtitle">Pending SRs</div>
              <div className="sr-count-value">{srData.openSRCount}</div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card closed"
              onClick={() =>
                navigate(buildComplaintsDrilldownQuery({ statusToken: 5 }))
              }
            >
              <div className="sr-count-label">Closed SR Count</div>
              <div className="sr-count-subtitle">Successfully closed SRs</div>
              <div className="sr-count-value">{srData.closedSRCount}</div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card rejected"
              onClick={() =>
                navigate(buildComplaintsDrilldownQuery({ statusToken: 4 }))
              }
            >
              <div className="sr-count-label">Rejected SR Count</div>
              <div className="sr-count-subtitle">Rejected SRs</div>
              <div className="sr-count-value">{srData.rejectedSRCount}</div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card open"
              onClick={() =>
                navigate(
                  buildComplaintsDrilldownQuery({
                    queryToRespond: true,
                    useDashboardStatusWhenNoToken: false,
                  })
                )
              }
            >
              <div className="sr-count-label">Queries to be responded by Me</div>
              <div className="sr-count-subtitle">
              Queries I need to respond
              </div>
              <div className="sr-count-value">{srData.queryToRespondSRCount}</div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card special"
              onClick={() =>
                navigate(
                  buildComplaintsDrilldownQuery({
                    specialCase: true,
                    useDashboardStatusWhenNoToken: false,
                  })
                )
              }
            >
              <div className="sr-count-label">Special Case SRs</div>
              <div className="sr-count-subtitle">
                Rejected and Reopened SR
              </div>
              <div className="sr-count-value">{srData.specialCaseSRCount}</div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card approved-payment-pending"
              onClick={() =>
                navigate(buildComplaintsDrilldownQuery({ statusToken: 7 }))
              }
            >
              <div className="sr-count-label">Approved & Payment Pending SR </div>
              <div className="sr-count-subtitle">Approved and yet to close - WIP</div>
              <div className="sr-count-value">{srData.approvedPaymentPendingSRCount}</div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="row charts-row mt-0">
          {/* Pie Chart Widget */}
          <div className="col-12 col-lg-6">
            <div className="pie-chart-widget">
              <div className="pie-chart-header">
                <h2 className="pie-chart-title">SR Status breakup</h2>
                <div className="chart-header-actions">
                  {renderChartCardDateFilter(srStatusCardDatePickerRef, srStatusCardDate.dateRange)}
                  <DashboardExportDropdown
                    onExcel={handlePieChartExportExcel}
                    onPdf={handlePieChartExportPdf}
                    onCsv={handlePieChartExportCsv}
                  />
                </div>
              </div>

              <div className="pie-chart-container">
                <div className="pie-chart-wrapper">
                  <Pie
                    ref={pieChartRef}
                    data={hasSrStatusData ? chartData : noDataChartData}
                    options={chartOptions}
                  />
                </div>

                <div className="pie-chart-legend">
                  <div className="legend-item">
                    <div className="legend-color open"></div>
                    <span className="legend-label open">Open SR Count</span>
                    <span className="legend-value">{srStatusBreakupData.openSRCount}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color closed"></div>
                    <span className="legend-label closed">Closed SR Count</span>
                    <span className="legend-value">{srStatusBreakupData.closedSRCount}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color rejected"></div>
                    <span className="legend-label rejected">Rejected SR Count</span>
                    <span className="legend-value">{srStatusBreakupData.rejectedSRCount}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color special"></div>
                    <span className="legend-label special">Special Case SRs</span>
                    <span className="legend-value">{srStatusBreakupData.specialCaseSRCount}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color approved-payment-pending"></div>
                    <span className="legend-label approved-payment-pending">
                      Approved & Payment Pending SR 
                    </span>
                    <span className="legend-value">{srStatusBreakupData.approvedPaymentPendingSRCount}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bar Chart Widget */}
          <div className="col-12 col-lg-6">
            <div className="bar-chart-widget">
              <div className="bar-chart-header">
                <h2 className="bar-chart-title">SR Category breakup</h2>
                <div className="chart-header-actions">
                  {renderChartCardDateFilter(srCategoryCardDatePickerRef, srCategoryCardDate.dateRange)}
                  <DashboardExportDropdown
                    onExcel={handleBarChartExportExcel}
                    onPdf={handleBarChartExportPdf}
                    onCsv={handleBarChartExportCsv}
                  />
                </div>
              </div>

              <div className="bar-chart-wrapper">
                <Bar
                  ref={barChartRef}
                  data={barChartData}
                  options={barChartOptions}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Top 10 Customers Chart Row */}
        <div className="row charts-row">
          <div className="col-12">
            <div className="bar-chart-widget">
              <div className="bar-chart-header">
                <h2 className="bar-chart-title">
                  Top 10 Customers by SR Count
                </h2>
                <div className="chart-header-actions">
                  {renderChartCardDateFilter(topCustomersCardDatePickerRef, topCustomersCardDate.dateRange)}
                  <DashboardExportDropdown
                    onExcel={handleTopCustomersExportExcel}
                    onPdf={handleTopCustomersExportPdf}
                    onCsv={handleTopCustomersExportCsv}
                  />
                </div>
              </div>

              <div className="bar-chart-wrapper top-customers-chart-wrapper">
                <Bar
                  ref={topCustomersChartRef}
                  data={topCustomersChartData}
                  options={topCustomersChartOptions}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
